import React from 'react';
import { motion } from 'framer-motion';
import { ListChecks, Activity, Compass, Target } from 'lucide-react';
import { AnimatedIcon, IconMotion } from './AnimatedIcon';

/**
 * Visão geral em orbes da Home (design minimal v1).
 * Cada orbe resume uma seção (estado atual visível mesmo fechado). Ao tocar, a seção detalhada abre abaixo.
 * Puramente visual: recebe números já calculados pelo StudyPlanHomeDashboard.
 */
export type HomePanel = 'tasks' | 'summary' | 'skills' | 'errors';

interface HomeOrbOverviewProps {
  open: HomePanel | null;
  onToggle: (panel: HomePanel) => void;
  tasksDone: number;
  tasksTotal: number;
  minutes: number;
  words: number;
  consistency: number;
  skillAvg: number;
  topSkill: string;
  pendingErrors: number;
  masteredErrors: number;
}

const ease = [0.22, 1, 0.36, 1] as const;

export const Orb: React.FC<{
  index: number;
  open: boolean;
  onClick: () => void;
  label: string;
  value: string;
  status: string;
  pct: number;
  tint: string;
  stroke: string;
  icon: React.ReactNode;
  motion: IconMotion;
}> = ({ index, open, onClick, label, value, status, pct, tint, stroke, icon, motion: m }) => {
  const R = 46;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  return (
    <motion.button
      onClick={onClick}
      aria-expanded={open}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: open ? 1.03 : 1 }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.55, ease, delay: index * 0.06 }}
      className="group relative aspect-square w-full rounded-full cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      {/* disco pastel interno */}
      <span className={`absolute rounded-full ${tint} transition-all duration-500 ${open ? 'inset-[6%]' : 'inset-[9%]'}`} />
      {/* anel de progresso */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={R} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-ink/10" />
        {clamped > 0 && (
          <motion.circle
            cx="50" cy="50" r={R} fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: clamped / 100 }}
            transition={{ duration: 1.1, ease, delay: 0.2 + index * 0.08 }}
          />
        )}
      </svg>
      {/* contorno quando aberto */}
      {open && (
        <motion.span
          layoutId="homeOrbSelected"
          className="absolute -inset-1.5 rounded-full border border-ink/40"
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        />
      )}
      {/* conteúdo */}
      <span className="relative z-10 flex flex-col items-center justify-center h-full px-3 text-ink">
        <span className="mb-1 opacity-70">
          <AnimatedIcon motion={m} active={open}>{icon}</AnimatedIcon>
        </span>
        <span className="font-display text-[30px] sm:text-4xl font-light tracking-[-0.05em] leading-none tabular-nums">{value}</span>
        <span className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ink/70">{label}</span>
        <span className="mt-1 text-[10px] sm:text-[11px] text-ink/55 leading-tight text-center line-clamp-2 max-w-[62%]">{status}</span>
      </span>
    </motion.button>
  );
};

export const HomeOrbOverview: React.FC<HomeOrbOverviewProps> = ({
  open, onToggle, tasksDone, tasksTotal, minutes, words, consistency, skillAvg, topSkill, pendingErrors, masteredErrors,
}) => {
  const tasksLeft = Math.max(0, tasksTotal - tasksDone);
  const errorsTotal = pendingErrors + masteredErrors;
  const orbs = [
    {
      id: 'tasks' as const, label: 'Tarefas', value: `${tasksDone}/${tasksTotal}`,
      status: tasksTotal > 0 && tasksLeft === 0 ? 'Concluído hoje' : `${tasksLeft} pendente${tasksLeft === 1 ? '' : 's'}`,
      pct: tasksTotal ? (tasksDone / tasksTotal) * 100 : 0, tint: 'bg-tint-sage', stroke: 'rgb(var(--tintd-sage))',
      icon: <ListChecks size={17} strokeWidth={1.6} />, motion: 'draw' as IconMotion,
    },
    {
      id: 'summary' as const, label: 'Atividade', value: `${minutes}m`,
      status: `${words} palavra${words === 1 ? '' : 's'}`,
      pct: consistency, tint: 'bg-tint-sky', stroke: 'rgb(var(--tintd-sky))',
      icon: <Activity size={17} strokeWidth={1.6} />, motion: 'pulse' as IconMotion,
    },
    {
      id: 'skills' as const, label: 'Habilidades', value: `${Math.round(skillAvg)}%`,
      status: skillAvg > 0 && topSkill ? `Forte: ${topSkill}` : 'Comece a praticar',
      pct: skillAvg, tint: 'bg-tint-lavender', stroke: 'rgb(var(--tintd-lavender))',
      icon: <Compass size={17} strokeWidth={1.6} />, motion: 'spin' as IconMotion,
    },
    {
      id: 'errors' as const, label: 'Erros', value: `${pendingErrors}`,
      status: pendingErrors > 0 ? 'Para revisar' : 'Tudo em dia',
      pct: errorsTotal ? (masteredErrors / errorsTotal) * 100 : 100, tint: 'bg-tint-rose', stroke: 'rgb(var(--tintd-rose))',
      icon: <Target size={17} strokeWidth={1.6} />, motion: 'wobble' as IconMotion,
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-3xl mx-auto px-1">
        {orbs.map((o, i) => (
          <Orb key={o.id} {...o} index={i} open={open === o.id} onClick={() => onToggle(o.id)} />
        ))}
      </div>
      <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
        {open ? 'Toque novamente para recolher' : 'Toque em um orbe para ver os detalhes'}
      </p>
    </div>
  );
};

export default HomeOrbOverview;
